import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

type LanguageCode = "es" | "en";

type LanguageModalContextValue = {
  openLanguageModal: () => void;
};

const LanguageModalContext = createContext<LanguageModalContextValue | null>(
  null
);

const normalizeLanguage = (language?: string | null): LanguageCode =>
  language?.startsWith("en") ? "en" : "es";

export const useLanguageModal = () => {
  const context = useContext(LanguageModalContext);
  if (!context) {
    throw new Error("useLanguageModal must be used within LanguageModalProvider");
  }
  return context;
};

const getSuggestedLanguage = () =>
  normalizeLanguage(navigator.languages?.[0] || navigator.language);

const LanguageModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [suggestedLanguage, setSuggestedLanguage] = useState<LanguageCode>("es");

  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang) {
      setSuggestedLanguage(normalizeLanguage(urlLang));
      localStorage.setItem("languageConfirmed", "true");
    }
    const storedLanguage = localStorage.getItem("language");
    if (!urlLang && !storedLanguage) {
      setSuggestedLanguage(getSuggestedLanguage());
    }
    if (storedLanguage) {
      setSuggestedLanguage(normalizeLanguage(storedLanguage));
    }
    const hasConfirmed = localStorage.getItem("languageConfirmed") === "true";
    if (!hasConfirmed) {
      setIsRequired(true);
      setIsOpen(true);
    }
  }, []);

  const openLanguageModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeLanguageModal = useCallback(() => {
    if (!isRequired) {
      setIsOpen(false);
    }
  }, [isRequired]);

  const handleSelectLanguage = useCallback(
    async (language: LanguageCode) => {
      localStorage.setItem("language", language);
      localStorage.removeItem("i18nextLng");
      localStorage.setItem("languageConfirmed", "true");
      await i18n.changeLanguage(language);
      const params = new URLSearchParams(window.location.search);
      if (params.has("lang") || window.location.pathname.startsWith("/guests/")) {
        params.set("lang", language);
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        );
      }
      window.location.reload();
      return;
      setIsRequired(false);
      setIsOpen(false);
    },
    [i18n]
  );

  const value = useMemo(
    () => ({
      openLanguageModal,
    }),
    [openLanguageModal]
  );

  return (
    <LanguageModalContext.Provider value={value}>
      {children}
      {isOpen && (
        <div className="modal-base">
          <div className="modal-content w-11/12 max-w-md">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-primary">
                  Elige tu idioma
                </h3>
                <p className="text-sm text-gray-600">Choose your language</p>
              </div>
              {!isRequired && (
                <button
                  onClick={closeLanguageModal}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              )}
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => handleSelectLanguage("es")}
                className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                  suggestedLanguage === "es"
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-primary border-primary hover:bg-primary hover:text-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">Español</span>
                  {suggestedLanguage === "es" && (
                    <span className="text-xs uppercase tracking-wide bg-white/20 px-2 py-1 rounded-full">
                      Sugerido
                    </span>
                  )}
                </div>
                <span className="text-sm opacity-80">Spanish</span>
              </button>
              <button
                onClick={() => handleSelectLanguage("en")}
                className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                  suggestedLanguage === "en"
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-primary border-primary hover:bg-primary hover:text-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">English</span>
                  {suggestedLanguage === "en" && (
                    <span className="text-xs uppercase tracking-wide bg-white/20 px-2 py-1 rounded-full">
                      Suggested
                    </span>
                  )}
                </div>
                <span className="text-sm opacity-80">Ingles</span>
              </button>
            </div>
            <p className="mt-4 text-xs text-gray-500 text-center">
              Puedes cambiarlo despues / You can change it later.
            </p>
            {isRequired && (
              <p className="mt-2 text-xs text-gray-500 text-center">
                Selecciona un idioma para continuar / Select a language to
                continue.
              </p>
            )}
          </div>
        </div>
      )}
    </LanguageModalContext.Provider>
  );
};

export default LanguageModalProvider;
